import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, CheckCircle2, AlertTriangle, Loader2, ExternalLink, ShieldCheck, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Stripe Connect (Express) host-side settings page.
 *
 * 3 visible states driven by GET /api/stripe-connect/status:
 *   - not connected           → "Connect Stripe" CTA
 *   - connected, incomplete   → "Continue Setup" CTA + warning
 *   - connected, complete     → "Stripe Connected" success card
 */
export default function StripeConnectSettings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);

  const authHeaders = () => {
    const token = localStorage.getItem('session_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchStatus = async () => {
    try {
      const res = await axios.get(`${API}/stripe-connect/status`, { headers: authHeaders() });
      setStatus(res.data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 401 || e?.response?.status === 403) {
        toast.error('Sign in as a host to manage payments.');
      } else if (detail) {
        toast.error(typeof detail === 'string' ? detail : 'Could not load Stripe status');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConnect = async () => {
    setBusy(true);
    try {
      // Step 1: ensure account exists (idempotent on backend)
      await axios.post(`${API}/stripe-connect/create-account`, {}, { headers: authHeaders() });
      // Step 2: mint a fresh onboarding link
      const linkRes = await axios.post(`${API}/stripe-connect/onboarding-link`, {}, { headers: authHeaders() });
      if (linkRes.data?.url) {
        window.location.href = linkRes.data.url;
        return;
      }
      throw new Error('No onboarding URL returned');
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Failed to start Stripe onboarding';
      toast.error(typeof detail === 'string' ? detail : 'Failed to start Stripe onboarding');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center" data-testid="stripe-connect-loading">
        <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  const connected = !!status?.connected;
  const complete = !!status?.onboarding_complete;
  const requirementsDue = status?.requirements_due || [];

  return (
    <div className="min-h-screen bg-[#050505] pb-16" data-testid="stripe-connect-page">
      <div className="border-b border-[#1A1A1A] bg-[#0A0A0A]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Button
            onClick={() => navigate(-1)}
            variant="ghost"
            className="mb-4 -ml-2 text-[#AFAFAF] hover:text-[#EAE7E1]"
            data-testid="stripe-connect-back-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-sm bg-[#0A0A0A] border border-[#1A1A1A] flex items-center justify-center">
              <CreditCard className="w-5 h-5 text-[#D4AF37]" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-[#EAE7E1] tracking-tight" data-testid="stripe-connect-heading">Payments</h1>
              <p className="text-sm text-[#AFAFAF] mt-1">Connect Stripe to accept card payments for paid events.</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {!connected && (
          <NotConnectedCard busy={busy} onConnect={handleConnect} />
        )}
        {connected && !complete && (
          <IncompleteCard busy={busy} onContinue={handleConnect} requirementsDue={requirementsDue} />
        )}
        {connected && complete && (
          <ConnectedCard status={status} />
        )}

        <div className="mt-6 rounded-sm border border-[#1A1A1A] bg-[#0A0A0A] p-5">
          <h3 className="text-xs uppercase tracking-wider text-[#777] font-semibold mb-2">How it works</h3>
          <ul className="text-[13px] text-[#AFAFAF] space-y-1.5 leading-relaxed">
            <li>• You connect a Stripe Express account in a few clicks.</li>
            <li>• Buyers pay via Stripe Checkout — card details never touch VelvetGate.</li>
            <li>• Stripe emails buyers a receipt automatically.</li>
            <li>• You stay in full control of your account on Stripe's dashboard.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

function NotConnectedCard({ busy, onConnect }) {
  return (
    <div className="rounded-sm border border-[#1A1A1A] bg-[#0A0A0A] p-6" data-testid="stripe-connect-not-connected-card">
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-full bg-[#1f1a0d] border border-[#5f4a1f] flex items-center justify-center flex-shrink-0">
          <CreditCard className="w-5 h-5 text-[#FFB800]" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-lg font-bold text-[#EAE7E1]" data-testid="stripe-connect-state-title">Stripe is not connected</h2>
          <p className="text-sm text-[#AFAFAF] mt-1.5 leading-relaxed">
            Connect your Stripe account to publish paid events and accept card payments.
          </p>
          <Button
            onClick={onConnect}
            disabled={busy}
            className="mt-5 bg-gradient-to-r from-[#8C6B1F] via-[#D4AF37] to-[#F2D27A] hover:opacity-90 text-[#050505] rounded-sm px-6 py-3 font-bold disabled:opacity-60"
            data-testid="stripe-connect-button"
          >
            {busy ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Starting…</>) : (<>Connect Stripe<ExternalLink className="w-4 h-4 ml-2" /></>)}
          </Button>
        </div>
      </div>
    </div>
  );
}

function IncompleteCard({ busy, onContinue, requirementsDue }) {
  return (
    <div className="rounded-sm border border-[#5f4a1f] bg-[#1a1610] p-6" data-testid="stripe-connect-incomplete-card">
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-full bg-[#1f1a0d] border border-[#5f4a1f] flex items-center justify-center flex-shrink-0">
          <AlertTriangle className="w-5 h-5 text-[#FFB800]" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-lg font-bold text-[#FFB800]" data-testid="stripe-connect-state-title">Finish Stripe onboarding to accept payments</h2>
          <p className="text-sm text-[#EAE7E1] mt-1.5 leading-relaxed">
            Stripe still needs a few details before you can accept payments. This usually takes 2–3 minutes.
          </p>
          {requirementsDue.length > 0 && (
            <div className="mt-3 rounded-sm border border-[#5f4a1f] bg-[#0A0A0A] p-3" data-testid="stripe-connect-requirements">
              <div className="text-[10px] uppercase tracking-wider text-[#FFB800] font-semibold mb-1">Outstanding items</div>
              <ul className="text-[12px] text-[#AFAFAF] space-y-0.5 list-disc list-inside">
                {requirementsDue.slice(0, 5).map((r) => <li key={r}>{r.replaceAll('_', ' ').replaceAll('.', ' › ')}</li>)}
              </ul>
            </div>
          )}
          <Button
            onClick={onContinue}
            disabled={busy}
            className="mt-5 bg-gradient-to-r from-[#8C6B1F] via-[#D4AF37] to-[#F2D27A] hover:opacity-90 text-[#050505] rounded-sm px-6 py-3 font-bold disabled:opacity-60"
            data-testid="stripe-connect-continue-button"
          >
            {busy ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Resuming…</>) : (<>Continue Setup<ExternalLink className="w-4 h-4 ml-2" /></>)}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ConnectedCard({ status }) {
  return (
    <div className="rounded-sm border border-[#1f5f44] bg-[#0d1a14] p-6" data-testid="stripe-connect-connected-card">
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-full bg-[#0f231a] border border-[#1f5f44] flex items-center justify-center flex-shrink-0">
          <ShieldCheck className="w-5 h-5 text-[#6EE7B7]" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-lg font-bold text-[#6EE7B7]" data-testid="stripe-connect-state-title">Stripe Connected</h2>
          <p className="text-sm text-[#EAE7E1] mt-1.5">Payments Enabled</p>
          <div className="mt-4 grid grid-cols-2 gap-2">
            <FlagRow label="Charges enabled" ok={status?.charges_enabled} testId="stripe-flag-charges" />
            <FlagRow label="Payouts enabled" ok={status?.payouts_enabled} testId="stripe-flag-payouts" />
          </div>
          {status?.account_id && (
            <p className="text-[11px] text-[#555] mt-3" data-testid="stripe-connect-account-id">
              Account: <span className="font-mono">{status.account_id}</span>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function FlagRow({ label, ok, testId }) {
  return (
    <div
      className={`rounded-sm border px-3 py-2 text-xs flex items-center gap-2 ${ok ? 'border-[#1f5f44] bg-[#0f231a] text-[#6EE7B7]' : 'border-[#5f1f1f] bg-[#221111] text-[#F87171]'}`}
      data-testid={testId}
    >
      <CheckCircle2 className={`w-3.5 h-3.5 ${ok ? '' : 'opacity-30'}`} />
      <span>{label}</span>
    </div>
  );
}
